import ChipItem from '@/components/elements/ChipItem';
import { Fab } from '@/components/elements/forms/buttons';
import Button from '@/components/elements/forms/buttons/Button/Button';
import Icon from '@/components/icons/Icon';
import ContainerLayout from '@/components/layouts/ContainerLayout';
import { MapTiler } from '@/components/modules/map';
import { DynamicMapProps } from '@/components/modules/map/MapTiler';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import { ROUTE_BUILDER } from '@/constants/pages';
import { Footer } from '@/features/footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import { getSelectedFilters } from '@/features/searchV2/components/Filter/Filter.helpers';
import useFilterManager from '@/features/searchV2/components/Filter/Filter.hooks';
import { MatchingSearchResultsCount } from '@/features/searchV2/components/MatchingSearchResultsCount';
import useSortingManager from '@/features/searchV2/components/Sorting/Sorting.hooks';
import SearchControls, { SearchControlProps } from '@/features/searchV2/modules/SearchControls/SearchControls';
import useSearchControlsManager from '@/features/searchV2/modules/SearchControls/SearchControls.hooks';
import SearchForm from '@/features/searchV2/modules/SearchForm/SearchForm';
import { getSearchParamsForMatchedServices } from '@/features/searchV2/pages/index.helpers';
import { classnames, isEmpty } from '@/helpers';
import { setBookingStartingPoint } from '@/helpers/analytics';
import { useAppSelector } from '@/hooks';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION, SEARCH_PAGE_VIEWS } from '@/hooks/search/useSearch.constants';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { Fragment, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MPLoadMorePaginator from '../components/MPLoadMorePaginator';
import { MPServiceCardSkeleton } from '../components/MPServiceCard/MPServiceCard';
import NoSearchResults from '../components/NoSearchResults';
import SearchWithoutDeal from '../components/SearchWithoutDeal';
import SerpHeader from '../components/SerpHeader';
import {
  createSerpCardClickHandlers,
  getBrandSerpHeaderImage,
  getDealsSearchHeadingProps,
  getDealsSerpBreadcrumbs,
  getSerpHeaderImage,
  renderDealCard,
  renderSistaminutenCard,
} from '../helpers';
import { useDealsSearchContext } from '../hooks/useDealsSearch';
import useDealsSearchForm from '../hooks/useDealsSearchForm';
import DealsSectionPreview from '../modules/DealSectionPreview';
import DealSearchControls, { DealSearchControlsProps } from '../modules/DealsSearchControls';
import { DEALS_GRADIENT_STOP } from './DealsSearchPage';
import { SKELETON_CARD_COUNT } from './DealsSearchResults.constants';
import { SerpCardItem, trackSerpCardClick } from '../tracking';
import { DealSearchItem, SistaminutenSearchItem } from '../types/dealSearch';
import { buildMapPlaces, renderMapItem, searchResultsContainerClassName } from './DealsSearchResults.helpers';
import DealsSearchSEO from './DealsSearchSEO';

const DealsSearchResults = () => {
  const searchManager = useDealsSearchContext();
  const { q, location } = searchManager;
  const deals = useAppSelector((state) => state.dealsSearch);
  const { isMobileView } = useMobileView();
  const megaMenuManager = useMegaMenuContext();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    keywordAutoSuggestManager,
    locationAutoSuggestManager,
    dateTimePickerManager,
    isSmtOnlySearch,
    setIsSmtOnlySearch,
  } = useDealsSearchForm({
    searchManager,
    loadLocationFromHistory: false,
  });

  const searchControlsScrollRef = useRef<HTMLDivElement>(null);
  const searchControlsIntersectionObserver = useIntersectionObserver(searchControlsScrollRef, { threshold: 1 });
  const [megaMenuVisible, setMegaMenuVisible] = useState(true);

  const showSearchControlsInNavbar = useMemo(() => {
    if (!isMobileView) return false;
    const { view } = searchManager.viewToggle;
    if (view === 'list' && searchControlsIntersectionObserver === 'aboveViewport') return true;
    if (view === 'map') return true;
    return false;
  }, [searchControlsIntersectionObserver, searchManager.viewToggle.view]);

  const handleToggleMap = useCallback(() => {
    const nextView = SEARCH_PAGE_VIEWS.find((v) => v.view !== searchManager.viewToggle.view);
    if (nextView) searchManager.viewToggle.setView({ ...nextView });
  }, [searchManager.viewToggle]);

  const searchControlManager = useSearchControlsManager(searchManager);

  const filterManager = useFilterManager({
    initialState: { prefs: searchManager.prefs, showOptions: false },
    urlPrefs: searchManager.prefs,
    applyOnToggle: !isMobileView,
    onChangeAppliedFilters: searchControlManager.onChangeAppliedFilters,
  });

  const selectedFilters = getSelectedFilters(searchManager.prefs);

  const sortingManager = useSortingManager({
    initialState: { sort: searchManager.sort, showOptions: false },
    urlSorting: searchManager.sort,
    onChangeSorting: searchControlManager.onChangeSorting,
    onClearSorting: searchControlManager.onClearSorting,
  });

  const dealSearchControlsProps: DealSearchControlsProps = {
    ...searchControlManager,
    searchManager,
    /**
     * If we show the filters/sorting from the SearchHeader we disable the 'showOptions'
     * in the <DealsSearchControls /> since we use the same filterManager/sortingManager
     * for both <SearchControls /> and <DealsSearchControls />
     *
     * this is to avoid showing showing two modals
     */
    sortingManager: {
      ...sortingManager,
      showOptions: showSearchControlsInNavbar ? false : sortingManager.showOptions,
    },
    filterManager: {
      ...filterManager,
      showOptions: showSearchControlsInNavbar ? false : filterManager.showOptions,
    },
    onToggleMap: handleToggleMap,
  };

  const searchControlsProps: SearchControlProps = {
    ...searchControlManager,
    searchManager,
    sortingManager,
    filterManager,
    showSistaminutenToggle: false,
  };

  function handleOnMapBoundsChange(bounds: string) {
    searchManager.performSearch({ page: 1 }, { bounds });
  }

  /**
   * Computed fields for JSX rendering
   */
  const list = deals.list;
  const items = list?.results.flatMap((r) => (r.type === 'organic' ? r.items : [])) ?? [];
  const total = list?.total ?? 0;
  const isFetching = deals.fetching || deals.previewRedirectType !== null;
  const isLoading = isFetching || searchManager.initialLoading;
  const extendedFetching = searchManager.extendedFetching;
  const hasMore = !list?.isLastPage;
  const currentPage = list?.page ?? 1;
  const pageSize = list?.pageSize ?? 0;
  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
  const mapVisible = searchManager.viewToggle.view === 'map';
  const breadcrumbs = getDealsSerpBreadcrumbs(q, location, searchManager.type);
  const headingProps = getDealsSearchHeadingProps(searchManager);
  const hasBrandLogoUrl = list?.type === 'brand_deals' ? list.template?.logoImageUrlSecondary : undefined;
  const showBrandLogo = Boolean(hasBrandLogoUrl) && searchManager.type === 'brand_deals';
  const shouldShowHeaderImage = !mapVisible;

  const headerImage = shouldShowHeaderImage
    ? showBrandLogo
      ? getBrandSerpHeaderImage(hasBrandLogoUrl, isMobileView)
      : getSerpHeaderImage(searchManager.type, isMobileView)
    : null;

  const showSkeleton = isLoading;
  const hasNoSearchResults = !isLoading && items.length === 0;
  const hasFetchedAllSearchResults = !hasNoSearchResults && items.length >= total;

  const serpType = list?.type ?? 'deals';
  const mapPlaces = buildMapPlaces(serpType, items);

  // A desktop popup keeps the click handler it was drawn with for as long as its place stays on the map, across
  // searches, so the click reads the current search through this ref.
  const mapCardClickRef = useRef<(item: SerpCardItem) => void>();
  mapCardClickRef.current = (item: SerpCardItem) => {
    const placeId = item.type === 'deal' ? item.data.place.id : item.data.id;
    const position = items.findIndex((listItem) => ('place' in listItem ? listItem.place.id : listItem.id) === placeId);
    trackSerpCardClick({
      searchManager,
      item,
      state: deals,
      screenName: 'deals_search_results_map',
      resultSurface: 'serp_map',
      position: position < 0 ? undefined : position,
    });
    setBookingStartingPoint('deals_search_results', searchManager.q);
  };
  const onMapCardClick = useCallback((item: SerpCardItem) => mapCardClickRef.current?.(item), []);

  const mapProps: DynamicMapProps = {
    places: mapPlaces,
    fetching: Boolean(isLoading),
    position: (searchManager.lat && searchManager.lon ? `${searchManager.lat}-${searchManager.lon}` : false) as
      | string
      | false,
    bounds: deals.bounds,
    fillContainer: true,
    showPopup: !isMobileView,
    renderMarkerPopup: renderMapItem(serpType, items, !isMobileView, onMapCardClick),
    renderCarouselItem: renderMapItem(serpType, items, !isMobileView, onMapCardClick),
    onMapBoundsChange: handleOnMapBoundsChange,
    // The map only calls this for its own cards; the deals cards above track their clicks themselves.
    onPlaceClick: () => {},
  };

  const { onDealClick, onMerchantClick } = createSerpCardClickHandlers(
    searchManager,
    deals,
    'deals_search_results',
    searchManager.saveDealsSearchState,
  );
  const onListDealClick = (item: DealSearchItem) => onDealClick(item, items.indexOf(item));
  const onListMerchantClick = (item: SistaminutenSearchItem) => onMerchantClick(item, items.indexOf(item));

  const renderCards = ({ renderInContainerLayout }: { renderInContainerLayout: boolean }) => {
    if (!list) return null;

    const renderCard = list.type === 'sistaminuten' ? renderSistaminutenCard : renderDealCard;

    const children: ReactNode[] = [];

    if (list.type === 'deals') {
      list.results.forEach((entry, index) => {
        if (entry.type === 'organic') {
          entry.items.forEach((item) => {
            children.push(renderDealCard({ item, dispatch, onClick: onListDealClick }));
          });
          return;
        }
        if (entry.type === 'sponsored' && !isEmpty(entry.section)) {
          children.push(
            <div
              key={`sponsored-${index}`}
              className={classnames('col-span-full', renderInContainerLayout && '-mx-md @2xl:mx-0')}>
              <DealsSectionPreview section={entry.section} dispatch={dispatch} />
            </div>,
          );
        }
      });
    } else {
      // sistaminuten + brand_deals: only organic entries
      list.results.forEach((entry) => {
        if (entry.type === 'organic') {
          entry.items.forEach((item) => {
            children.push(
              renderCard({
                item,
                searchParams: getSearchParamsForMatchedServices({ ...searchManager }),
                dispatch,
                onClick: list.type === 'sistaminuten' ? onListMerchantClick : onListDealClick,
              }),
            );
          });
        }
      });
    }

    const grid = (
      <div className={searchResultsContainerClassName} data-deals-results-grid="">
        {children}
      </div>
    );

    return renderInContainerLayout ? (
      <ContainerLayout onlyAbove2xl>
        <div className="py-md px-md">{grid}</div>
      </ContainerLayout>
    ) : (
      <div className="@container">{grid}</div>
    );
  };

  const megaMenu = (
    <MegaMenu
      {...megaMenuManager}
      renderNavigationContainer={(props) => {
        const ref = props.navigationContainerRef.current;
        return ref ? (
          createPortal(<MegaMenu.Categories {...props} />, ref)
        ) : (
          /**
           * need to render the navigation before the ref is mounted to make sure navigation appears on initial render
           */
          <div id={props.navigationContainerId} className="mega-menu-navigation-container" role="navigation">
            <MegaMenu.Categories {...props} />
          </div>
        );
      }}
    />
  );

  const navBarShared = (
    <NavBar
      source="list"
      navClass="list-container navigation"
      defaultNavBarHeight={isMobileView ? 120 : 128}
      view={searchManager.viewToggle.view}
      middleSlot={
        <SearchForm
          searchManager={searchManager}
          keywordAutoSuggestManager={keywordAutoSuggestManager}
          locationAutoSuggestManager={locationAutoSuggestManager}
          dateTimePickerManager={dateTimePickerManager}
          isSmtOnlySearch={isSmtOnlySearch}
          setIsSmtOnlySearch={setIsSmtOnlySearch}
        />
      }
      bottomSlot={
        <Fragment>
          {(() => {
            if (showSearchControlsInNavbar) {
              return (
                <div className="space-x-sm no-scrollbar pb-sm pt-xxs flex overflow-x-scroll">
                  <SearchControls {...searchControlsProps} />
                  {isMobileView && (
                    <>
                      {searchManager.viewToggle.render}
                      {/* just a spacer for horizontal scroll */}
                      <div className="pl-sm"></div>
                    </>
                  )}
                </div>
              );
            }

            if (megaMenuManager.activeTab) {
              return megaMenu;
            }
            /** if ref is not mounted use 56px for now for for Mega menu tabs (update if component changes) */
            const height = megaMenuManager.navRef.current?.getBoundingClientRect()?.height ?? 56;
            return (
              <HideOnScrollWrapper height={height} onScrollChangeCallback={setMegaMenuVisible}>
                {megaMenu}
              </HideOnScrollWrapper>
            );
          })()}
        </Fragment>
      }
      floatingSlot={megaMenuManager.renderNavigationContainer(megaMenuManager)}
    />
  );

  const searchControlsShared = (
    <Fragment>
      <div
        className={classnames(
          'pt-md border-border_regular flex items-center justify-between border-t',
          !isMobileView && 'gap-sm',
        )}>
        <span className="gap-xs flex items-center">
          <MatchingSearchResultsCount {...searchManager} loading={showSkeleton} results={total} />
        </span>

        {/* FILTERS AND VIEW TOGGLE  with ref to check when filters should appear in SearchHeader */}
        <div ref={searchControlsScrollRef} className="gap-x-sm flex items-center">
          <DealSearchControls
            {...dealSearchControlsProps}
            showMapToggle={!isMobileView && searchManager.viewToggle.view === 'list'}
          />
        </div>
      </div>
      {/* Render selected filters */}
      {selectedFilters.length > 0 && (
        <div id="selected-filters" className="gap-xs no-scrollbar flex flex-row overflow-x-scroll">
          {selectedFilters.map((filter) => {
            return (
              <ChipItem
                key={filter.id}
                as="button"
                label={filter.label}
                selected
                withSelectedIcon
                selectedIcon="close"
                className="flex-shrink-0"
                onClick={searchControlManager.onRemoveSingleFilter(filter.id)}
              />
            );
          })}
        </div>
      )}
    </Fragment>
  );

  // Mobile fullscreen map view
  if (isMobileView && mapVisible) {
    return (
      <div className="bg-gradient-deals" style={DEALS_GRADIENT_STOP}>
        {navBarShared}
        <div className="relative h-screen w-full">
          <MapTiler {...mapProps} />
        </div>
      </div>
    );
  }

  // Desktop map view with two column layout (List + Map)
  if (!isMobileView && mapVisible) {
    return (
      <div className="bg-gradient-deals" style={DEALS_GRADIENT_STOP}>
        {navBarShared}
        <div className="justify-end md:flex ">
          {/* Left: listings with manual left padding mimicking ContainerLayout */}
          <div className="pr-xl basis-[60%] pl-[max(calc((100vw-1440px)/2),24px)]">
            <div className="py-lg md:py-xl md:pl-xl">
              <SerpHeader
                breadcrumbs={breadcrumbs}
                heading={
                  <Fragment>
                    <span className="text-fg_primary font-bold">{headingProps.keyword}, </span>
                    <span className="text-fg_secondary whitespace-nowrap">{headingProps.location}</span>
                  </Fragment>
                }
                seoText={headingProps.description}
                image={headerImage}
              />
            </div>

            <div className="py-md space-y-lg md:pl-xl">
              {searchControlsShared}

              {isLoading ? (
                <div className="gap-lg grid grid-cols-1 md:grid-cols-2">
                  {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                    <MPServiceCardSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <>
                  <div>{renderCards({ renderInContainerLayout: false })}</div>

                  <MPLoadMorePaginator
                    currentPage={currentPage}
                    totalPages={totalPages}
                    hasMore={hasMore}
                    loading={extendedFetching}
                    fetching={deals.fetching}
                    onLoadMore={searchManager.extendSearchResults}
                    onPageChange={searchManager.navigateToPage}
                  />
                </>
              )}
            </div>
            <Footer />
          </div>

          {/* Right: map — extends to screen edge, no padding */}
          <div
            className={classnames('sticky flex-1 basis-[40%] transition-all duration-200')}
            style={{
              top: megaMenuVisible ? 128 : 72,
              height: megaMenuVisible ? 'calc(100vh - 128px)' : 'calc(100vh - 72px)',
            }}>
            <Fab
              size="md"
              icon={<Icon variant="close" onClick={handleToggleMap} />}
              className="right-lg top-lg absolute z-10"
            />
            <MapTiler {...mapProps} />
          </div>
        </div>
      </div>
    );
  }

  // Default layout — ContainerLayout with grid
  return (
    <div className="bg-gradient-deals" style={DEALS_GRADIENT_STOP}>
      {navBarShared}
      <div className={classnames('py-lg md:py-xl', isMobileView && 'pb-0')}>
        <ContainerLayout onlyAbove2xl className="px-md">
          <SerpHeader
            breadcrumbs={breadcrumbs}
            heading={
              <Fragment>
                <span className="text-fg_primary font-bold">{headingProps.keyword}, </span>
                <span className="text-fg_secondary whitespace-nowrap">{headingProps.location}</span>
              </Fragment>
            }
            seoText={headingProps.description}
            image={headerImage}
          />
          {isMobileView && (
            <Button
              variant="primary"
              onClick={handleToggleMap}
              rightIcon={
                <Icon
                  variant="map"
                  style={{ filter: 'var(--filter-color-fg-primary-inverse)', width: 16, height: 16 }}
                />
              }>
              <span>{_t('mapView')}</span>
            </Button>
          )}
        </ContainerLayout>
      </div>
      <ContainerLayout onlyAbove2xl>
        <div className="py-md space-y-lg px-md">
          {searchControlsShared}

          {showSkeleton && (
            <div className={searchResultsContainerClassName}>
              {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                <MPServiceCardSkeleton key={i} />
              ))}
            </div>
          )}
        </div>
      </ContainerLayout>

      {!showSkeleton && renderCards({ renderInContainerLayout: true })}

      <ContainerLayout onlyAbove2xl>
        <div className="py-md space-y-lg px-md">
          <MPLoadMorePaginator
            currentPage={currentPage}
            totalPages={totalPages}
            hasMore={hasMore}
            loading={extendedFetching}
            fetching={deals.fetching}
            onLoadMore={searchManager.extendSearchResults}
            onPageChange={searchManager.navigateToPage}
          />

          {hasNoSearchResults && (
            <NoSearchResults
              keyword={searchManager.q}
              location={searchManager.location}
              onCTAClick={() =>
                history.push(
                  ROUTE_BUILDER.serp(
                    searchManager.q || DEFAULT_SEARCH_KEYWORD,
                    searchManager.location || DEFAULT_SEARCH_LOCATION,
                  ),
                )
              }
            />
          )}

          {hasFetchedAllSearchResults && (
            <SearchWithoutDeal
              onCTAClick={() =>
                history.push(
                  ROUTE_BUILDER.serp(
                    searchManager.q || DEFAULT_SEARCH_KEYWORD,
                    searchManager.location || DEFAULT_SEARCH_LOCATION,
                  ),
                )
              }
              {...(searchManager.prefs && {
                onClearFilters: () => {
                  searchControlManager.onClearFilters();
                },
              })}
            />
          )}
        </div>
      </ContainerLayout>

      <ContainerLayout onlyAbove2xl className="pt-cv-margin">
        <div className="px-md">
          <Footer />
        </div>
      </ContainerLayout>
      <DealsSearchSEO {...searchManager} preview={deals.preview} list={deals.list} />
    </div>
  );
};

export default DealsSearchResults;
